// Abonelik paketleri.
//
// Paketler backend'den gelir:
//   POST /api/SubscriptionPlan/GetSubscriptionPlanList { targetType, category }
// Aşağıdaki FALLBACK_PLANS yalnızca API'ye ulaşılamazsa kullanılır.
//
// PARA BİRİMİ: Tüketiciye TL gösterilmesi zorunlu. Öncelik sırası:
//   1. API'den gelen `priceTry`  (tercih edilen, tahsil edilen tutarla birebir)
//   2. currency === 'TRY' ise `price`
//   3. Aksi hâlde `price` (USD) × USD_TRY_RATE, yalnızca geçici çözüm
// 3. yola düşülüyorsa gösterilen tutar ile iyzico'nun çektiği tutar ayrışabilir.

export const USD_TRY_RATE = 46;

// Satılan hizmetin hukuki tanımı, bir "İşe Yerleştirme Garantisi" veya iş
// bulma/aracılık hizmeti değil, Veri Analiz ve Form Taslak Hazırlama
// Yazılımı lisansıdır (SaaS). Form taslakları yalnızca kullanıcının onayıyla
// gönderilir. Paket kartlarında ve ödeme adımında değişmeden gösterilir.
export const SERVICE_DISCLAIMER =
  'Satın alınan hizmet, kişiselleştirilmiş Veri Analiz ve Form Taslak Hazırlama Yazılımı kullanım lisansıdır. Form taslakları yalnızca sizin onayınızla gönderilir. Resmi işe aracılık veya işe yerleştirme garantisi içermez.';

// API İngilizce plan adı → Türkçe UI adı
// "X İlana Otomatik Başvuru" tarzı eski adlandırma işe yerleştirme/aracılık
// algısı yarattığı için "AI Kariyer <Kademe>" markasına, kota tanımı da
// "AI Profil Analizi & Otomatik Eşleştirme" diline çevrildi.
const PLAN_NAMES_TR = {
  Freemium: 'AI Kariyer Başlangıç',
  Starter: 'AI Kariyer Starter',
  Pro: 'AI Kariyer Pro',
};

const SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

export const formatPrice = (price, currency = 'TRY') => {
  if (!price) return 'Ücretsiz';
  const amount = price.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount} ${SYMBOLS[currency] || currency}`;
};

// API'den gelen planı UI'ın beklediği şekle çevirir
export function normalizePlan(raw) {
  // BE alan adını camelCase döndürüyor; PascalCase de kabul edilsin
  const priceTry = raw.priceTry ?? raw.PriceTry ?? null;
  const hasTry = priceTry !== null && priceTry !== undefined;

  const isFree = raw.planType === 'Free' || (hasTry ? !priceTry : !raw.price);

  let inTry;
  if (hasTry) inTry = priceTry;
  else if (raw.currency === 'TRY') inTry = raw.price;
  else inTry = (raw.price || 0) * USD_TRY_RATE;

  // API açıklaması zaten limiti anlatıyor; ayrıca kredi satırı eklemek tekrar olur.
  // FALLBACK_PLANS birden fazla madde göstermek için `features` dizisini
  // doğrudan sağlayabilir, API bu alanı döndürmediği için canlı planlar
  // her zaman tek maddeye (description) düşer.
  const features = Array.isArray(raw.features) && raw.features.length
    ? raw.features
    : (raw.description ? [raw.description] : []);

  return {
    id: raw.id,
    name: PLAN_NAMES_TR[raw.name] || raw.name,
    price: Number((inTry || 0).toFixed(2)),
    currency: 'TRY',
    // Fiyat TL olarak mı geldi, yoksa kurla mı çevrildi (teşhis için)
    priceConverted: !hasTry && raw.currency !== 'TRY',
    period: raw.planType === 'Monthly' ? 'ay' : null,
    summary: raw.description || '',
    features,
    isFree,
    isUnlimited: Boolean(raw.isUnlimited),
    creditCount: raw.creditCount,
    sortingId: raw.sortingId ?? 0,
  };
}

// API'ye ulaşılamazsa gösterilecek paketler (canlı verinin aynısı).
// Kotalar "ilan başvurusu" değil "ilan analizi / form taslağı" üzerinden
// anlatılır, paket bedelini başvuru adedine bağlamak İŞKUR mevzuatına
// aykırıdır (başvuru karşılığı ücret alınamaz). Fiyatlar doğrudan TL.
export const FALLBACK_PLANS = [
  {
    id: 'fallback-free',
    name: 'Freemium',
    description: 'Ayda 5 ilan analizi ve form taslağı hazırlama',
    planType: 'Free',
    price: 0,
    currency: 'TRY',
    creditCount: 5,
    isUnlimited: false,
    sortingId: 1,
  },
  {
    id: 'fallback-starter',
    name: 'Starter',
    description: 'Ayda 50 adede kadar AI ilan analizi ve onaylı form taslağı',
    features: [
      'Ayda 50 adede kadar AI ilan analizi ve onaylı form taslağı',
      'İlana özel CV optimizasyonu ve ATS skoru analizi',
    ],
    planType: 'Monthly',
    price: 189,
    currency: 'TRY',
    creditCount: 50,
    isUnlimited: false,
    sortingId: 2,
  },
  {
    id: 'fallback-pro',
    name: 'Pro',
    description: 'Sınırsız AI ilan analizi ve otomatik taslak hazırlama',
    features: [
      'Sınırsız AI ilan analizi ve otomatik taslak hazırlama',
      'Aylık 5 saat ücretsiz TransMeet toplantı çevirmeni ve AI Mülakat Simülasyonu',
      'Öncelikli eşleştirme ve anlık bildirimler',
    ],
    planType: 'Monthly',
    price: 499,
    currency: 'TRY',
    creditCount: null,
    isUnlimited: true,
    sortingId: 3,
  },
].map(normalizePlan);

// Ortadaki paket öne çıkarılır
export const isPopular = (plan, all) =>
  all.length > 2 && plan.sortingId === 2;
